import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Trash, UploadSimple, User, Star } from "@phosphor-icons/react";
import {
  createCharacter,
  deleteCharacter,
  addOutfit,
  deleteOutfit,
  uploadOutfitImage,
  setHeroImage,
  fileUrl,
} from "@/lib/api";
import { toast } from "sonner";
import MainCharactersPanel from "@/components/MainCharactersPanel";
import { VisualTagField } from "@/components/VisualTagField";

function AddCharacterForm({ storyId, onAdded }) {
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const c = await createCharacter(storyId, name.trim());
      toast.success(`Character "${name}" added`);
      setName("");
      onAdded(c);
    } catch (e) {
      toast.error("Failed to add character");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex gap-2 border border-dashed border-slate-800 rounded-sm p-3 items-center">
      <Input
        data-testid="new-character-name-input"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Character name (e.g. TemporalVixen)"
        className="bg-slate-950 border-slate-800 rounded-sm"
        onKeyDown={(e) => e.key === "Enter" && submit()}
      />
      <Button data-testid="new-character-submit-btn" onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-500 rounded-sm shrink-0">
        <Plus size={16} className="mr-1" /> Add
      </Button>
    </div>
  );
}

function AddOutfitDialog({ character, onAdded }) {
  const [open, setOpen] = useState(false);
  const [outfitName, setOutfitName] = useState("");
  const [descriptor, setDescriptor] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!outfitName.trim() || !descriptor.trim()) {
      toast.error("Outfit name and descriptor are required");
      return;
    }
    setSaving(true);
    try {
      const updated = await addOutfit(character.id, outfitName.trim(), descriptor.trim());
      toast.success("Outfit reference set locked");
      setOutfitName("");
      setDescriptor("");
      setOpen(false);
      onAdded(updated);
    } catch (e) {
      toast.error("Failed to add outfit");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid={`add-outfit-btn-${character.id}`} variant="outline" className="border-slate-800 rounded-sm text-xs w-full">
          <Plus size={14} className="mr-1" /> Add Outfit Reference Set
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-wider">
            Stage 2 — Lock Outfit for {character.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            data-testid="new-outfit-name-input"
            value={outfitName}
            onChange={(e) => setOutfitName(e.target.value)}
            placeholder="Outfit name (e.g. Train of Regret)"
            className="bg-slate-950 border-slate-800 rounded-sm"
          />
          <Textarea
            data-testid="new-outfit-descriptor-input"
            value={descriptor}
            onChange={(e) => setDescriptor(e.target.value)}
            placeholder="Verbatim locked descriptor block: hair color/texture/style, skin tone, eye color, outfit, proportions, accessories..."
            rows={5}
            className="bg-slate-950 border-slate-800 rounded-sm font-mono text-sm"
          />
        </div>
        <DialogFooter>
          <Button data-testid="new-outfit-submit-btn" onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-500 rounded-sm w-full">
            {saving ? "Locking…" : "Lock Reference Set"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OutfitCard({ character, outfit, onChanged }) {
  const inputRef = React.useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const updated = await uploadOutfitImage(character.id, outfit.outfit_id, file);
      toast.success("Reference image uploaded");
      onChanged(updated);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      e.target.value = "";
    }
  };

  const removeOutfit = async () => {
    try {
      const updated = await deleteOutfit(character.id, outfit.outfit_id);
      toast.success("Outfit reference removed");
      onChanged(updated);
    } catch (err) {
      toast.error("Failed to delete outfit");
    }
  };

  const markHero = async (path) => {
    try {
      const updated = await setHeroImage(character.id, outfit.outfit_id, path);
      toast.success("Hero (full-body) reference set — this is the image sent when only 1 slot is available");
      onChanged(updated);
    } catch (err) {
      toast.error("Failed to set hero image");
    }
  };

  return (
    <div data-testid={`outfit-card-${outfit.outfit_id}`} className="border border-slate-800 rounded-sm p-3 bg-slate-950/50 space-y-2">
      <div className="flex items-center justify-between">
        <span className="font-mono text-xs uppercase tracking-widest text-blue-400">{outfit.outfit_name}</span>
        <button
          data-testid={`delete-outfit-btn-${outfit.outfit_id}`}
          onClick={removeOutfit}
          className="text-slate-600 hover:text-rose-500 transition-colors"
        >
          <Trash size={14} />
        </button>
      </div>
      <p className="text-xs text-slate-400 leading-relaxed line-clamp-4">{outfit.descriptor}</p>
      <div className="grid grid-cols-3 gap-1.5">
        {outfit.image_paths.map((p) => {
          const isHero = outfit.hero_image_path === p;
          return (
            <div key={p} className="relative group">
              <img
                src={fileUrl(p)}
                alt={outfit.outfit_name}
                className={`w-full h-16 object-cover rounded-sm border ${isHero ? "border-amber-400" : "border-slate-800"}`}
              />
              {isHero && (
                <span data-testid={`hero-badge-${outfit.outfit_id}`} className="absolute top-0.5 left-0.5 flex items-center gap-0.5 bg-amber-500/90 text-slate-950 text-[9px] font-mono uppercase px-1 rounded-sm">
                  <Star size={8} weight="fill" /> Hero
                </span>
              )}
              {!isHero && (
                <button
                  data-testid={`set-hero-btn-${outfit.outfit_id}-${p}`}
                  onClick={() => markHero(p)}
                  title="Mark as hero (full-body) reference — sent first when only 1 image slot is available"
                  className="absolute inset-0 flex items-center justify-center bg-slate-950/70 opacity-0 group-hover:opacity-100 transition-opacity text-amber-400 text-[9px] font-mono uppercase"
                >
                  <Star size={12} className="mr-1" /> Set Hero
                </button>
              )}
            </div>
          );
        })}
        <button
          data-testid={`upload-image-btn-${outfit.outfit_id}`}
          onClick={() => inputRef.current.click()}
          className="w-full h-16 flex items-center justify-center border border-dashed border-slate-700 rounded-sm text-slate-500 hover:text-blue-400 hover:border-blue-500/50 transition-colors"
        >
          <UploadSimple size={16} />
        </button>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
      </div>
      <p className="text-[10px] text-slate-600 italic">
        Hero image = the full-body shot sent when a multi-character shot only has 1 reference slot for
        this character. Hover an image to mark it hero.
      </p>
    </div>
  );
}

export default function CharacterLockPanel({ storyId, characters, setCharacters }) {
  const handleAdded = (c) => setCharacters((prev) => (prev.some((p) => p.id === c.id) ? prev.map((p) => (p.id === c.id ? c : p)) : [...prev, c]));
  const handleUpdated = (updated) => setCharacters((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
  const handleDelete = async (id) => {
    try {
      await deleteCharacter(id);
      setCharacters((prev) => prev.filter((c) => c.id !== id));
      toast.success("Character removed");
    } catch (e) {
      toast.error("Failed to delete character");
    }
  };

  return (
    <div data-testid="character-lock-panel" className="space-y-4">
      <MainCharactersPanel storyId={storyId} characters={characters} setCharacters={setCharacters} />
      <div className="flex items-center justify-between">
        <h2 className="font-heading uppercase tracking-wider text-sm font-semibold text-slate-300">
          Stage 2 — Character Lock
        </h2>
      </div>
      <AddCharacterForm storyId={storyId} onAdded={handleAdded} />
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {characters.filter((c) => !c.is_main).map((c) => (
          <div key={c.id} data-testid={`character-card-${c.id}`} className="border border-slate-800 rounded-sm bg-slate-900/50 p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <User size={16} className="text-slate-500" />
                <span className="font-semibold text-sm text-slate-50">{c.name}</span>
              </div>
              <button
                data-testid={`delete-character-btn-${c.id}`}
                onClick={() => handleDelete(c.id)}
                className="text-slate-600 hover:text-rose-500 transition-colors"
              >
                <Trash size={14} />
              </button>
            </div>
            <div className="space-y-2">
              {c.outfits.map((o) => (
                <OutfitCard key={o.outfit_id} character={c} outfit={o} onChanged={handleUpdated} />
              ))}
            </div>
            <VisualTagField character={c} onChanged={handleUpdated} />
            <AddOutfitDialog character={c} onAdded={handleUpdated} />
          </div>
        ))}
        {characters.filter((c) => !c.is_main).length === 0 && (
          <div className="col-span-full text-center py-8 text-slate-600 text-sm border border-dashed border-slate-800 rounded-sm">
            No supporting characters added yet. (Optional — use this for characters beyond the 2 main leads above.)
          </div>
        )}
      </div>
    </div>
  );
}
